import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import moment from 'moment';
import { userService } from "../../../_services";
import FilterTagCom from '../FilterTag.jsx';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Download, BarChart3, List } from "lucide-react";

// Import modernized widgets
import BarWidget from '../BarWidget';
import LineWidget from '../LineWidget';
import ColumnPieWidget from '../ColumnPieWidget';
import MultiLineWidget from '../MultiLineWidget';
import ClaimWidget from '../Claim';
import TableWidget from '../TableWidget';
import MultiStackBarWidget from '../MultiStackBarWidget';
import DistStackBarWidget from '../DistStackBarWidget';

/**
 * User Dashboard Component
 * Modernized with React hooks - Displays widgets for distributor analytics
 */
function Dashboard({ startDate: propStartDate, endDate: propEndDate, dist_usertype, handeChangefilter, filterKey }) {
    // Initialize dates
    const getInitialStartDate = () => {
        const stored = localStorage.getItem('User_Start_Date');
        if (stored) return moment(stored).format("YYYYMMDD");
        return moment(new Date().setDate(new Date().getDate() - 30)).format("YYYYMMDD");
    };

    const getInitialEndDate = () => {
        const stored = localStorage.getItem('User_End_Date');
        return stored ? moment(stored).format("YYYYMMDD") : moment(new Date()).format("YYYYMMDD");
    };

    // State
    const [startDate, setStartDate] = useState(getInitialStartDate());
    const [endDate, setEndDate] = useState(getInitialEndDate());
    const [graphData, setGraphData] = useState([]);
    const [filterTagList, setFilterTagList] = useState('');
    const [roleType, setRoleType] = useState('Wholesaler');
    const [roleName, setRoleName] = useState('');
    const [loggedInUserId, setLoggedInUserId] = useState('0');
    const [counter, setCounter] = useState(0);
    const [legendValues, setLegendValues] = useState({});
    const [activeModes, setActiveModes] = useState({});
    const [widgetHasData, setWidgetHasData] = useState({});
    const exportHandlersRef = useRef({});
    const whId = '61';

    useEffect(() => {
        const usermetaData = window.atob(localStorage.getItem('ajs_user_id'));
        const [userId] = usermetaData.split(':');
        setLoggedInUserId(userId);
        setRoleName(userId);
        setRoleType(window.atob(localStorage.getItem('_Host_Pro_type')));

        const filterDataLocal = localStorage.getItem('Advance_Filter');
        setFilterTagList(filterDataLocal && filterDataLocal !== 'null' ? filterDataLocal : '');

        const newDate = new Date();
        localStorage.setItem('Filter_Last_Update_Time', newDate.toString());

        loadDashboardData();
    }, []);

    useEffect(() => {
        const propStartFormatted = moment(propStartDate).format("YYYYMMDD");
        const propEndFormatted = moment(propEndDate).format("YYYYMMDD");
        const prevHDType = localStorage.getItem('Prev_HD_Type');
        const updatedHDType = localStorage.getItem('Updated_HD_Type');
        const filterLastUpdate = localStorage.getItem('Filter_Last_Update_Time');
        const filterPrevUpdate = localStorage.getItem('Filter_Prev_Update_Time');

        if (
            startDate !== propStartFormatted ||
            endDate !== propEndFormatted ||
            prevHDType !== updatedHDType ||
            filterLastUpdate !== filterPrevUpdate
        ) {
            setStartDate(propStartFormatted);
            setEndDate(propEndFormatted);
            localStorage.setItem('dist_usertype', dist_usertype);

            if (prevHDType !== updatedHDType) {
                localStorage.setItem('Prev_HD_Type', updatedHDType);
            }

            if (filterLastUpdate !== filterPrevUpdate) {
                localStorage.setItem('Filter_Prev_Update_Time', filterLastUpdate);
            }

            loadDashboardData();
        }
    }, [propStartDate, propEndDate, dist_usertype]);

    // Re-run loadDashboardData whenever the parent signals a filter/tab/search change via filterKey.
    useEffect(() => {
        if (filterKey === undefined) return; // no-op when prop is absent
        loadDashboardData();
    }, [filterKey]);

    const loadDashboardData = () => {
        const useStartDate = propStartDate ? moment(propStartDate).format("YYYYMMDD") : startDate;
        const useEndDate = propEndDate ? moment(propEndDate).format("YYYYMMDD") : endDate;

        // Read userId directly from localStorage to avoid stale state closure
        let currentUserId = '0';
        try {
            const usermetaData = window.atob(localStorage.getItem('ajs_user_id'));
            currentUserId = usermetaData.split(':')[0];
        } catch (e) { /* ignore */ }

        let apiBaseURL = '/api/userDirect';
        if (dist_usertype === 'All') apiBaseURL = '/api/userAll';
        else if (dist_usertype === 'Override') apiBaseURL = '/api/userOverride';

        let adFilterJson = {};
        let stringifiedFilter = '';
        try {
            const filterData = localStorage.getItem('Advance_Filter');
            if (filterData && filterData !== 'null') {
                adFilterJson = JSON.parse(filterData);
                stringifiedFilter = JSON.stringify(adFilterJson);
                setFilterTagList(stringifiedFilter);
            } else {
                setFilterTagList('');
            }
        } catch (error) {
            console.error('Error parsing filter:', error);
            setFilterTagList('');
        }

        if (!stringifiedFilter) stringifiedFilter = JSON.stringify(adFilterJson);
        const filterTime = localStorage.getItem('Filter_Last_Update_Time');
        const claimType = localStorage.getItem('Updated_HD_Type');

        // Build widget configurations
        const widgets = [
            {
                url: `${apiBaseURL}/total_transactions`,
                    type: "Bar_List",
                    title: "Total Transactions",
                    Subtitle: "Total number of transactions for each day",
                    DataType: "NA",
                    XlabelString: "Date",
                    YlabelString: "# of Transactions",
                    interval: 'day',
                    id: "WTotalTransaction",
                RequestJson: JSON.stringify({
                    wname: currentUserId,
                    whname: whId,
                    start_date: useStartDate,
                    end_date: useEndDate,
                    rcount: 0,
                    chart_type: "columnList",
                    ispatientsaving: "true",
                    pharmacynumber: "all",
                    filtertime: filterTime,
                    rxsClaimType: claimType
                }),
                RequestJson1: stringifiedFilter
            },
            {
                url: `${apiBaseURL}/RXSClaims`,
                    type: "Claim",
                title: "",
                    Subtitle: "",
                    id: "RxSensClaim",
                RequestJson: JSON.stringify({
                    wname: currentUserId,
                    whname: whId,
                    start_date: useStartDate,
                    end_date: useEndDate,
                    rcount: 0,
                    chart_type: "list",
                    filtertime: filterTime,
                    rxsClaimType: claimType,
                    wholesaler_id: whId
                }),
                RequestJson1: stringifiedFilter
            },
            {
                url: `${apiBaseURL}/group_number_transactions`,
                    type: "Bar_List",
                    title: "Transactions by Group Number",
                    Subtitle: "Number of transactions for each group number",
                    DataType: "GroupNumber",
                    XlabelString: "Group Number",
                    YlabelString: "# of Transactions",
                    id: "WGroupNumberTransaction",
                RequestJson: JSON.stringify({
                    wname: currentUserId,
                    whname: whId,
                    start_date: useStartDate,
                    end_date: useEndDate,
                    rcount: 30,
                    chart_type: "columnList",
                    ispatientsaving: "true",
                    pharmacynumber: "all",
                    filtertime: filterTime,
                    rxsClaimType: claimType,
                    wholesaler_id: whId
                }),
                RequestJson1: stringifiedFilter
            },
            {
                url: `${apiBaseURL}/avg_PaitentSavings_values_percent`,
                    type: "Line_List",
                    title: "Average Patient Savings ($)",
                    Subtitle: "Average savings for all patients in $",
                    DataType: "NA",
                    XlabelString: "Date",
                    YlabelString: "Total Savings",
                    hideXTicks: true,
                    id: "WPatientSavings_Value",
                RequestJson: JSON.stringify({
                    wname: currentUserId,
                    whname: whId,
                    start_date: useStartDate,
                    end_date: useEndDate,
                    rcount: 0,
                    chart_type: "lineList",
                    ispatientsaving: "true",
                    pharmacynumber: "all",
                    filtertime: filterTime,
                    rxsClaimType: claimType,
                    wholesaler_id: whId
                }),
                RequestJson1: stringifiedFilter
            },
            {
                url: `${apiBaseURL}/avg_PaitentSavings_values_percent`,
                    type: "Line_List",
                    title: "Average Patient Savings (%)",
                    Subtitle: "Average savings for all patients in %",
                    DataType: "NA",
                    XlabelString: "Date",
                    YlabelString: "Total Savings",
                    hideXTicks: true,
                    id: "WPatientSavings_Percent",
                RequestJson: JSON.stringify({
                    wname: currentUserId,
                    whname: whId,
                    start_date: useStartDate,
                    end_date: useEndDate,
                    rcount: 0,
                    chart_type: "lineList",
                    ispatientsaving: "false",
                    pharmacynumber: "all",
                    filtertime: filterTime,
                    rxsClaimType: claimType,
                    wholesaler_id: whId
                }),
                RequestJson1: stringifiedFilter
            },
            {
                url: `${apiBaseURL}/state_transaction`,
                    type: "Column_Pie",
                    title: "Transactions by State",
                    Subtitle: "Number of transactions in each state",
                    DataType: "DrugName",
                    XlabelString: "Date",
                    YlabelString: "# of Unique Member ID's",
                    interval: 'day',
                    id: "DStateTransaction",
                RequestJson: JSON.stringify({
                    wname: currentUserId,
                    whname: whId,
                    start_date: useStartDate,
                    end_date: useEndDate,
                    rcount: 30,
                    chart_type: "pielist",
                    ispatientsaving: "true",
                    pharmacynumber: "all",
                    filtertime: filterTime,
                    rxsClaimType: claimType,
                    wholesaler_id: whId
                }),
                RequestJson1: stringifiedFilter
            },
            {
                url: `${apiBaseURL}/pharmacy_transaction`,
                    type: "List",
                    title: "Transactions by Pharmacy",
                    Subtitle: "Number of transactions at each pharmacy",
                    ColorCode: "",
                    XlabelString: "",
                    YlabelString: "",
                    DataType: "PharmacyName",
                    id: "DPharmacyTransaction",
                    BorderRadius: 0,
                RequestJson: JSON.stringify({
                    wname: currentUserId,
                    whname: whId,
                    start_date: useStartDate,
                    end_date: useEndDate,
                    rcount: 30,
                    chart_type: "list",
                    filtertime: filterTime,
                    rxsClaimType: claimType
                }),
                RequestJson1: stringifiedFilter
            },
            {
                url: `${apiBaseURL}/drug_transaction`,
                    type: "List",
                    title: "Transactions by Drug",
                    Subtitle: "Number of transactions for each drug",
                    ColorCode: "",
                    XlabelString: "",
                    YlabelString: "",
                    DataType: "DrugName",
                    id: "DDrugTransaction",
                    BorderRadius: 0,
                RequestJson: JSON.stringify({
                    wname: currentUserId,
                    whname: whId,
                    start_date: useStartDate,
                    end_date: useEndDate,
                    rcount: 30,
                    chart_type: "list",
                    ispatientsaving: "true",
                    pharmacynumber: "all",
                    filtertime: filterTime,
                    rxsClaimType: claimType,
                    wholesaler_id: whId
                }),
                RequestJson1: stringifiedFilter
            },
            {
                url: `${apiBaseURL}/earned_commission`,
                    type: "MultiStackBar_List",
                    title: "Total Revenue Earned",
                    Subtitle: "Revenue earned for each month",
                    DataType: "NA",
                    XlabelString: "Date",
                    YlabelString: "Commission",
                    interval: 'month',
                    id: "WEarnedCommission",
                RequestJson: JSON.stringify({
                    wname: currentUserId,
                    whname: whId,
                    start_date: useStartDate,
                    end_date: useEndDate,
                    rcount: 0,
                    chart_type: "column_multiaxislist",
                    ispatientsaving: "true",
                    pharmacynumber: "all",
                    filtertime: filterTime,
                    rxsClaimType: claimType
                }),
                RequestJson1: stringifiedFilter
            },
            {
                url: `${apiBaseURL}/dist_subdist_transactions`,
                    type: "List",
                    title: "Transactions (Sub-Distributors)",
                    Subtitle: "Transactions including sub-distributors",
                    XlabelString: "",
                    YlabelString: "",
                    ColorCode: "",
                    DataType: "NA",
                    id: "DTransactions",
                    BorderRadius: 0,
                RequestJson: JSON.stringify({
                    wname: currentUserId,
                    whname: whId,
                    start_date: useStartDate,
                    end_date: useEndDate,
                    rcount: 30,
                    chart_type: "list",
                    filtertime: filterTime,
                    rxsClaimType: claimType
                }),
                RequestJson1: stringifiedFilter
            },
            {
                url: `${apiBaseURL}/prescriber_transaction`,
                    type: "List",
                    title: "Transactions by Prescriber",
                    Subtitle: "Number of transactions at each Prescriber",
                    ColorCode: "",
                    XlabelString: "",
                    YlabelString: "",
                    DataType: "Prescriber",
                    id: "WPrescriberTransaction",
                BorderRadius: 0,
                RequestJson: JSON.stringify({
                    wname: currentUserId,
                    whname: whId,
                    start_date: useStartDate,
                    end_date: useEndDate,
                    rcount: 30,
                    chart_type: "list",
                    filtertime: filterTime,
                    rxsClaimType: claimType
                }),
                RequestJson1: stringifiedFilter
            },
            {
                url: `${apiBaseURL}/pharmacy_patient_savings`,
                    type: "List",
                    title: "Patient Savings by Pharmacy",
                Subtitle: "Patient savings in $ at each pharmacy",
                    ColorCode: "",
                    XlabelString: "",
                    YlabelString: "",
                    DataType: "PharmacyName",
                    id: "DPatientSavingTransaction",
                    BorderRadius: 0,
                RequestJson: JSON.stringify({
                    wname: currentUserId,
                    whname: whId,
                    start_date: useStartDate,
                    end_date: useEndDate,
                    rcount: 30,
                    chart_type: "list",
                    filtertime: filterTime,
                    rxsClaimType: claimType
                }),
                RequestJson1: stringifiedFilter
            },
            {
                url: `${apiBaseURL}/repeat_prescriptions_transactions`,
                    type: "List",
                title: "Transactions by Repeat Prescriptions",
                Subtitle: "Number of repeat prescriptions by each Prescriber",
                    ColorCode: "",
                    XlabelString: "",
                    YlabelString: "",
                    DataType: "Prescriber",
                    id: "WRepeat_PrescriptionsTransaction",
                BorderRadius: 0,
                RequestJson: JSON.stringify({
                    wname: currentUserId,
                    whname: whId,
                    start_date: useStartDate,
                    end_date: useEndDate,
                    rcount: 30,
                    chart_type: "list",
                    filtertime: filterTime,
                    rxsClaimType: claimType
                }),
                RequestJson1: stringifiedFilter
            }
        ];

        setWidgetHasData({});
        setLegendValues({});
        setGraphData(widgets);
        setCounter(prev => prev + 1);
    };

    const handleChangeA = (e) => {
        if (handeChangefilter) {
            handeChangefilter(e);
        }
    };

    // Called by each widget when it loads legend data (number shown alongside title)
    const handleLegendUpdate = useCallback((widgetId, value) => {
        setLegendValues(prev => ({ ...prev, [widgetId]: value }));
    }, []);

    // Called by each widget when its data is ready — registers export function
    const makeExportReady = useCallback((widgetId) => (fn) => {
        exportHandlersRef.current[widgetId] = fn;
    }, []);

    // Called by each widget after loading — reports whether it has actual data
    const makeDataReady = useCallback((widgetId) => (hasData) => {
        setWidgetHasData(prev => ({ ...prev, [widgetId]: hasData }));
    }, []);

    // Toggle chart/table mode for dual-mode widgets
    const handleModeChange = useCallback((widgetId, mode) => {
        setActiveModes(prev => ({ ...prev, [widgetId]: mode }));
    }, []);

    // Get widget layout classes
    const getWidgetClasses = (widgetId) => {
        const baseClasses = "bg-card rounded-lg border border-border shadow-sm";
        
        const layoutMap = {
            'WTotalTransaction': 'col-span-12',
            'RxSensClaim': 'col-span-12',
            'WGroupNumberTransaction': 'col-span-12 2xl:col-span-6',
            'WPatientSavings_Value': 'col-span-12 2xl:col-span-6',
            'WPatientSavings_Percent': 'col-span-12 2xl:col-span-6',
            'DStateTransaction': 'col-span-12 2xl:col-span-6',
            'DPharmacyTransaction': 'col-span-12',
            'DDrugTransaction': 'col-span-12 2xl:col-span-6',
            'WEarnedCommission': 'col-span-12 2xl:col-span-6',
            'DTransactions': 'col-span-12',
            'WPrescriberTransaction': 'col-span-12',
            'DPatientSavingTransaction': 'col-span-12 2xl:col-span-6',
            'WRepeat_PrescriptionsTransaction': 'col-span-12 2xl:col-span-6'
        };

        return `${baseClasses} ${layoutMap[widgetId] || 'col-span-12'}`;
    };

    // Widget types that support chart/table toggle
    const DUAL_MODE_TYPES = new Set(['Bar_List', 'Line_List', 'MultiLine_List', 'MultiStackBar_List', 'Column_Pie']);

    // Map widget type → readable chart label for tooltip
    const getChartLabel = (type) => {
        const map = { Bar_List: 'Bar', Line_List: 'Line', MultiLine_List: 'Line', MultiStackBar_List: 'Bar', Column_Pie: 'Pie' };
        return map[type] || 'Chart';
    };

    // Render widget based on type
    const renderWidget = (widget, index) => {
        const widgetProps = {
            data: widget,
            _Index: index,
            handlechangea: handleChangeA,
            onLegendUpdate: handleLegendUpdate,
            onExportReady: makeExportReady(widget.id),
            onDataReady: makeDataReady(widget.id),
            mode: activeModes[widget.id] || 'chart'
        };
        const key = `${widget.type}-${index}`;

        switch (widget.type) {
            case 'Bar_List':
                return <BarWidget key={key} {...widgetProps} />;
            case 'Line_List':
                return <LineWidget key={key} {...widgetProps} />;
            case 'Column_Pie':
                return <ColumnPieWidget key={key} {...widgetProps} />;
            case 'MultiLine_List':
                return <MultiLineWidget key={key} {...widgetProps} />;
            case 'Claim':
                return <ClaimWidget key={key} {...widgetProps} />;
            case 'List':
                return <TableWidget key={key} {...widgetProps} />;
            case 'MultiStackBar_List':
                return <DistStackBarWidget key={key} {...widgetProps} />;
            default:
                return null;
        }
    };

    // Auth check
    if (userService.Check_Null_Empty(localStorage.getItem("ajs_user_id"))) {
        return <Navigate to="/signup" replace />;
    }

    if (window.atob(localStorage.getItem('UserRole')) !== "Distributor") {
        return <Navigate to="/page-not-found" replace />;
    }

                                                        return (
        <div className="w-full space-y-6">
            {/* Filter Tags */}
            <FilterTagCom 
                Rolename={roleName}
                wh_Id={whId}
                Roletype={roleType}
                df={startDate}
                dt={endDate}
                handlechangea={handleChangeA}
                FilterTags={filterTagList}
                Loading={true}
            />

            {/* Widgets Grid */}
            <div className="grid grid-cols-12 gap-6">
                {graphData.map((widget, index) => {
                    // Special handling for Claim widget - no parent card wrapper
                    if (widget.type === 'Claim') {
                        return (
                            <div 
                                key={`widget-${widget.id}-${index}`}
                                className="col-span-12"
                            >
                                <ClaimWidget 
                                    data={widget}
                                    key={`${widget.type}-${index}`}
                                    _Index={index}
                                    handlechangea={handleChangeA}
                                />
                            </div>
                        );
                    }
                    
                    // Regular widgets with card wrapper
                    return (
                        <Card 
                            key={`widget-${widget.id}-${index}`}
                            className={getWidgetClasses(widget.id)}
                        >
                            <CardHeader className="border-b px-6 py-4">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex-1 min-w-0">
                                        <CardTitle className="text-lg font-semibold text-primary flex items-center gap-2 flex-wrap">
                                            {legendValues[widget.id] && (
                                                <span className="text-accent font-bold">
                                                    {legendValues[widget.id]}
                                                </span>
                                            )}
                                    {widget.title}
                                </CardTitle>
                                {widget.Subtitle && (
                                            <CardDescription className="text-sm text-accent mt-1">
                                        {widget.Subtitle}
                                    </CardDescription>
                                )}
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        {/* Export button */}
                                        <TooltipProvider>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <Button
                                                        variant="outline"
                                                        size="icon"
                                                        className="h-8 w-8 text-primary border-primary/20 hover:bg-muted"
                                                        disabled={widgetHasData[widget.id] !== true}
                                                        onClick={() => exportHandlersRef.current[widget.id]?.()}
                                                    >
                                                        <Download className="h-4 w-4" />
                                                    </Button>
                                                </TooltipTrigger>
                                                <TooltipContent>Download Report</TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                        {/* Chart / Table toggle — only for dual-mode widgets */}
                                        {DUAL_MODE_TYPES.has(widget.type) && (
                                            <TooltipProvider>
                                                <div className={`flex items-center rounded-md bg-muted p-1 gap-0.5 ${widgetHasData[widget.id] !== true ? 'opacity-50 pointer-events-none' : ''}`}>
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className={`h-7 w-7 transition-colors ${(activeModes[widget.id] || 'chart') === 'chart' ? 'bg-accent text-white hover:bg-accent hover:text-white' : 'text-primary hover:bg-transparent hover:text-primary'}`}
                                                                onClick={() => handleModeChange(widget.id, 'chart')}
                                                            >
                                                                <BarChart3 className="h-3.5 w-3.5" />
                                                            </Button>
                                                        </TooltipTrigger>
                                                        <TooltipContent>{getChartLabel(widget.type)}</TooltipContent>
                                                    </Tooltip>
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className={`h-7 w-7 transition-colors ${(activeModes[widget.id] || 'chart') === 'table' ? 'bg-accent text-white hover:bg-accent hover:text-white' : 'text-primary hover:bg-transparent hover:text-primary'}`}
                                                                onClick={() => handleModeChange(widget.id, 'table')}
                                                            >
                                                                <List className="h-3.5 w-3.5" />
                                                            </Button>
                                                        </TooltipTrigger>
                                                        <TooltipContent>List</TooltipContent>
                                                    </Tooltip>
                                                </div>
                                            </TooltipProvider>
                                        )}
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="p-6 overflow-hidden">
                                {renderWidget(widget, index)}
                            </CardContent>
                        </Card>
                    );
                })}
                                            </div>
                                        </div>
    );
}

export default Dashboard;
